'use client';

import { type ReactNode } from 'react';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import type { TransformedPlantsData, PlantsComponent } from '@/types/plants';
import PlantsHero from '../components/PlantsHero';
import {
  PlantSectionWithHeadingBlock,
  PlantsVideoSectionBlock,
} from '../components/ManufacturingPlants';
import GrindingUnits from '../components/GrindingUnits';

interface PlantsClientProps {
  locale: string;
  plantsData: TransformedPlantsData | null;
  /**
   * Streaming slot for the FAQ + enquiry block. The lead-forms Strapi fetch
   * happens in an async Server Component in `page.tsx` and is wrapped in
   * `<Suspense>`, so above-fold content paints without waiting on it.
   */
  faqSlot?: ReactNode;
}

function renderPlantsComponent(component: PlantsComponent, faqSlot?: ReactNode) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={component.id} zone={component} />;

    case 'plants.plant-sectionwith-heading':
      return <PlantSectionWithHeadingBlock key={component.id} section={component} />;

    case 'homepage.video-section':
      return <PlantsVideoSectionBlock key={component.id} section={component} />;

    case 'plants.griding-unit-section':
      return (
        <GrindingUnits
          key={component.id}
          data={{
            heading: component.heading ?? '',
            subHeadingHtml: component.subHeading ?? '',
            units: component.unit ?? [],
          }}
        />
      );

    case 'modules.faqwith-form':
      return faqSlot ? <div key={component.id}>{faqSlot}</div> : null;

    default:
      return null;
  }
}

export default function PlantsClient({ locale: _locale, plantsData, faqSlot }: PlantsClientProps) {
  if (!plantsData) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 sm:px-6">
        <div className="text-center">
          <h1 className="mb-3 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
            Our Plants
          </h1>
          <p className="text-base text-gray-600 sm:text-lg md:text-xl">
            Sorry, we couldn&apos;t load the plants page content. Please try again later.
          </p>
        </div>
      </main>
    );
  }

  const components = plantsData.components ?? [];

  return (
    <main className="min-h-screen overflow-x-hidden">
      <PlantsHero hero={plantsData.hero} />

      {components.map((component) => renderPlantsComponent(component, faqSlot))}
    </main>
  );
}
